import { useMemo, useState } from 'react';
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Delete,
  DragIndicator,
  Edit,
  PlayCircleOutline,
} from '@mui/icons-material';

import {
  deleteTutorial,
  reorderTutorials,
  TutorialDependency,
} from '../../services/TutorialService';
import { displayNameForUser } from '../../utils/UserDisplayUtils';
import { formatDateTime, formatFileSize } from '../../utils/DiactiveUtils';
import ConfirmDeleteDialog from '../common/ConfirmDeleteDialog';
import TutorialPreviewDialog from './TutorialPreviewDialog';

const TutorialsTable = ({
  items,
  showAppAccessColumn,
  userNamesById,
  onEdit,
  onRefresh,
  onError,
}) => {
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [previewTarget, setPreviewTarget] = useState(null);
  const [previewIndex, setPreviewIndex] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [localOrder, setLocalOrder] = useState(null);

  const sorted = useMemo(
    () => [...items].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [items]
  );

  const displayList = localOrder ?? sorted;

  const persistOrder = async (reordered) => {
    setReordering(true);
    try {
      await reorderTutorials(reordered.map((t) => t.id));
      setLocalOrder(null);
      onRefresh();
    }
    catch {
      setLocalOrder(null);
      onError?.('No se pudo cambiar el orden');
    }
    finally {
      setReordering(false);
    }
  };

  const handleDragStart = (index) => {
    if (reordering) return;
    setDragIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === index || reordering) return;

    const next = [...displayList];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(index, 0, moved);
    setDragIndex(index);
    setLocalOrder(next);
  };

  const handleDragEnd = async () => {
    if (dragIndex === null) return;
    setDragIndex(null);

    const finalList = localOrder ?? sorted;
    const idsChanged = finalList.some((t, i) => t.id !== sorted[i]?.id);
    if (idsChanged) {
      await persistOrder(finalList);
    }
    else {
      setLocalOrder(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteTutorial(deleteTarget.id);
      setDeleteTarget(null);
      onRefresh();
    }
    catch {
      onError?.('No se pudo eliminar el tutorial');
    }
    finally {
      setDeleting(false);
    }
  };

  const colSpan = showAppAccessColumn ? 7 : 6;

  return (
    <Box sx={{ opacity: reordering ? 0.6 : 1, pointerEvents: reordering ? 'none' : 'auto' }}>
      <TableContainer>
        <Table sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 48 }} />
              <TableCell sx={{ width: 44 }}>#</TableCell>
              <TableCell>Título</TableCell>
              {showAppAccessColumn && (
                <TableCell sx={{ width: 120 }} align="center">
                  <Tooltip title="Solo pacientes con acceso a la app Diactive-1">
                    <span>Acceso App</span>
                  </Tooltip>
                </TableCell>
              )}
              <TableCell sx={{ width: 110 }}>Tamaño</TableCell>
              <TableCell sx={{ width: 150 }}>
                <Tooltip title="Fecha y persona de la última modificación">
                  <span>Actualizado</span>
                </Tooltip>
              </TableCell>
              <TableCell sx={{ width: 160 }} align="center">
                Acciones
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayList.map((item, index) => {
              const updatedById = item.updatedById || item.uploaderId;
              const updatedByName = displayNameForUser(userNamesById, updatedById);
              const requiresApp =
                item.dependency === TutorialDependency.HasAppAccess;

              return (
                <TableRow
                  key={item.id}
                  hover
                  draggable={!reordering}
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  sx={{
                    cursor: reordering ? 'default' : 'grab',
                    bgcolor: dragIndex === index ? 'action.selected' : undefined,
                    '&:active': { cursor: reordering ? 'default' : 'grabbing' },
                  }}
                >
                  <TableCell sx={{ width: 48, px: 0.5 }}>
                    <DragIndicator color="action" fontSize="small" />
                  </TableCell>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {item.title}
                    </Typography>
                  </TableCell>
                  {showAppAccessColumn && (
                    <TableCell align="center">
                      <Typography variant="body2" color={requiresApp ? 'primary.main' : 'text.secondary'}>
                        {requiresApp ? 'Sí' : '—'}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell sx={{ width: 110 }}>{formatFileSize(item.sizeBytes ?? 0)}</TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap>
                      {formatDateTime(item.updatedAt)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap display="block">
                      {updatedByName ?? '—'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ width: 160 }} align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.25 }}>
                      <Tooltip title="Vista previa">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => {
                            setPreviewTarget(item);
                            setPreviewIndex(index);
                          }}
                          disabled={reordering || !item.storagePath}
                        >
                          <PlayCircleOutline fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => onEdit(item)}
                          disabled={reordering}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteTarget(item)}
                          disabled={reordering}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
            {displayList.length === 0 && (
              <TableRow>
                <TableCell colSpan={colSpan} align="center">
                  <Typography variant="body1" color="text.secondary" sx={{ py: 2 }}>
                    No hay tutoriales
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TutorialPreviewDialog
        open={!!previewTarget}
        tutorial={previewTarget}
        orderIndex={previewIndex}
        onClose={() => {
          setPreviewTarget(null);
          setPreviewIndex(null);
        }}
      />

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        title="Eliminar tutorial"
        description="Se eliminará el vídeo y los datos del tutorial de forma permanente. Esta acción no se puede deshacer."
        loading={deleting}
        onCancel={() => !deleting && setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
      />
    </Box>
  );
};

export default TutorialsTable;
